<template>
  <transition name="fade">
    <div v-if="visible" class="fixed" @click="closePopup">
      <div class="modal-card" @click.stop>
        <div class="modal-header">
          <h2 class="title">{{ formData.id === 0 ? 'Crear plan' : 'Editar plan' }}</h2>
          <button type="button" class="close-btn" @click="closePopup">&times;</button>
        </div>

        <form @submit.prevent="handleSubmit" class="form">
          <div class="form-group">
            <label for="plan-name">Nombre del plan <span class="required">*</span></label>
            <input
              id="plan-name"
              v-model="formData.name"
              type="text"
              placeholder="Ej: Taller de cocina italiana"
              required
            />
          </div>

          <div class="form-group">
            <label for="plan-description">Descripción</label>
            <textarea
              id="plan-description"
              v-model="formData.description"
              rows="3"
              placeholder="Añade una descripción detallada del plan..."
            ></textarea>
          </div>

          <div class="form-group">
            <div class="radio-pill-group">
              <label
                class="radio-pill"
                :class="{ 'is-selected': date_type === 'any' }"
              >
                <input
                  type="radio"
                  value="any"
                  v-model="date_type"
                />
                <span>Cualquier fecha</span>
              </label>
              <label
                class="radio-pill"
                :class="{ 'is-selected': date_type === 'defined' }"
              >
                <input
                  type="radio"
                  value="defined"
                  v-model="date_type"
                />
                <span>Definir fechas</span>
              </label>
            </div>
          </div>

          <div v-if="date_type === 'defined'">
            <div class="new-date-container" v-for="(date, index) in formData.dates" :key="index">
              <div class="form-row">
                <div class="form-group col">
                  <label :for="`plan-date-${index}`">Título</label>
                  <input
                    :id="`plan-title-${index}`"
                    v-model="date[2]"
                    type="text"
                  />
                </div>
              </div>
              <div class="form-row" style="margin-top: 10px">
                <div class="form-group col">
                  <label :for="`plan-date-${index}`">Fecha de realización</label>
                  <input
                    :id="`plan-date-${index}`"
                    v-model="date[0]"
                    type="date"
                  />
                </div>

                <div class="form-group col">
                  <label for="plan-hour">Hora</label>
                  <input
                    id="plan-hour"
                    v-model="date[1]"
                    type="time"
                  />
                </div>
                <span class="delete-date-btn" @click="formData.dates.splice(index, 1)">✗</span>
              </div>
            </div>
            <span  class="add-date-btn" @click="formData.dates.push(['', ''])">+ Añadir fecha</span>

          </div>
          <div class="form-group" v-if="date_type === 'any'">
            <label for="plan-expiration">Fecha límite de reserva / fin</label>
            <input
              id="plan-expiration"
              v-model="formData.expiration_date"
              type="date"
            />
          </div>

          <div class="form-row">
            <div class="form-group col">
              <label for="plan-capacity">Plazas / Aforo</label>
              <input
                id="plan-capacity"
                v-model.number="formData.capacity"
                type="number"
                min="0"
                placeholder="0"
              />
            </div>

            <div class="form-group col">
              <label for="plan-price">Precio (€)</label>
              <input
                id="plan-price"
                v-model.number="formData.price"
                type="number"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="plan-observations">Observaciones / Más información</label>
            <textarea
              id="plan-observations"
              v-model="formData.observations"
              rows="3"
              placeholder="Añade observaciones..."
            ></textarea>
          </div>

          <!-- Gestión de imágenes -->
          <div class="form-group">
            <div class="images-header">
              <label>Imágenes del plan ({{ formData.images.length }})</label>
              <label for="plan-image-upload" class="upload-btn">
                <span>+ Añadir imágenes</span>
                <input
                  id="plan-image-upload"
                  type="file"
                  multiple
                  accept="image/*"
                  class="hidden-input"
                  @change="handleImageUpload"
                />
              </label>
            </div>

            <div v-if="formData.images && formData.images.length" class="images-preview">
              <div v-for="(img, index) in formData.images" :key="index" class="image-thumb">
                <img :src="getImageSrc(img)" alt="Imagen del plan" />
                <button
                  type="button"
                  class="remove-image-btn"
                  title="Eliminar imagen"
                  aria-label="Eliminar imagen"
                  @click="removeImage(index)"
                >
                  <svg viewBox="0 0 24 24" width="12" height="12" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" fill="none">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </button>
              </div>
            </div>
            <p v-else class="empty-images-note">No hay imágenes añadidas aún.</p>
          </div>

          <div class="form-actions">
            <div class="form-actions-left">
              <button
                v-if="formData.id && formData.id !== 0 && formData.id !== '0'"
                type="button"
                class="delete-plan-btn"
                @click="handleDelete"
              >
                Eliminar plan
              </button>
            </div>
            <div class="form-actions-right">
              <button type="button" class="secondary_button" @click="closePopup">
                Cancelar
              </button>
              <button type="submit" class="main_button-green">
                Guardar
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, reactive, watch, defineProps, defineEmits } from 'vue';
import { useRouter } from 'vue-router';
import { useLoadingStore } from "@/stores/loadingStore";
import { refreshToken } from "@/services/authService";

const loadingStore = useLoadingStore();
const router = useRouter();

const props = defineProps({
  plan: {
    type: Object,
  },
});

const emit = defineEmits(['close', 'saved', 'error']);

const visible = ref(false);

const formData = reactive({
  id: 0,
  name: '',
  description: '',
  dates: [],
  capacity: 0,
  price: 0,
  expiration_date: '',
  observations: '',
  images: [],
});

const date_type = ref('any');

const formatDatesForInput = (dates = []) => {
  if (!Array.isArray(dates)) return [];
  return dates.map(d => [formatDateForInput(d.date), formatHourForInput(d.hour), d.title]);
};

const formatDateForInput = (val) => {
  if (!val) return '';
  // Si viene en formato ISO o YYYY-MM-DD HH:mm:ss, extraer la parte YYYY-MM-DD
  return String(val).split('T')[0].split(' ')[0];
};

const formatHourForInput = (val) => {
  if (!val) return '';
  // Si viene en formato HH:mm:ss, recortar a HH:mm
  const parts = String(val).split(':');
  if (parts.length >= 2) {
    return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
  }
  return val;
};

watch(
  () => props.plan,
  (newVal) => {
    visible.value = !!newVal;
    if (newVal) {
      formData.id = newVal.id ?? 0;
      formData.name = newVal.name || '';
      formData.description = newVal.description || '';

      let parsedDates = [];
      if (Array.isArray(newVal.dates)) {
        parsedDates = newVal.dates;
      } else if (typeof newVal.dates === 'string') {
        try {
          parsedDates = JSON.parse(newVal.dates);
        } catch (e) {
          parsedDates = [];
        }
      }

      formData.dates = formatDatesForInput(parsedDates);
      date_type.value = formData.dates.length === 0 ? 'any' : 'defined';
      formData.capacity = newVal.capacity ?? 0;
      formData.price = newVal.price ?? 0;
      formData.expiration_date = formatDateForInput(newVal.expiration_date);
      formData.observations = newVal.observations || '';
      formData.images = Array.isArray(newVal.images) ? [...newVal.images] : [];
    }
  },
  { immediate: true, deep: true }
);

const getImageSrc = (img) => {
  if (typeof img === 'string') return img;
  return img.image_url || img.url || '';
};

const handleImageUpload = (event) => {
  const files = event.target.files;
  if (!files || !files.length) return;

  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const previewUrl = URL.createObjectURL(file);
    // Guardamos la referencia al File y la URL de preview
    formData.images.push({
      file,
      image_url: previewUrl,
      is_new: true,
    });
  }

  // Reset del input para permitir volver a seleccionar el mismo archivo si se desea
  event.target.value = '';
};

const removeImage = (index) => {
  const target = formData.images[index];
  // Si fue generada con URL.createObjectURL la revocamos
  if (target && target.is_new && target.image_url && target.image_url.startsWith('blob:')) {
    URL.revokeObjectURL(target.image_url);
  }
  formData.images.splice(index, 1);
};

const closePopup = () => {
  visible.value = false;
  emit('close');
};

const handleSubmit = async () => {
  loadingStore.setLoading(true);

  try {
    let response = await saveDraftPlan();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await saveDraftPlan();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      emit("error", "Se ha producido un error guardando los detalles del plan.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      emit("error", data.message);
    } else {
      emit('saved', data.message);
      closePopup();
    }
  } catch (exception) {
    emit("error", exception.message);
  } finally {
    loadingStore.setLoading(false);
  }
}

const handleDelete = async () => {
  if (!window.confirm("¿Estás seguro de que deseas eliminar este plan?")) {
    return;
  }

  loadingStore.setLoading(true);

  try {
    let response = await deleteDraftPlan();

    if (response.status === 401) {
      const refreshed = await refreshToken();
      if (refreshed) {
        response = await deleteDraftPlan();
      } else {
        loadingStore.setLoading(false);
        localStorage.removeItem('user_id');
        localStorage.removeItem('token');
        router.push('/');
        return;
      }
    }

    if (!response.ok) {
      emit("error", "Se ha producido un error eliminando el plan.");
      return;
    }

    const data = await response.json();

    if (data.errors) {
      emit("error", data.message);
    } else {
      emit('saved', data.message || "Plan eliminado correctamente");
      closePopup();
    }
  } catch (exception) {
    emit("error", exception.message);
  } finally {
    loadingStore.setLoading(false);
  }
};

async function deleteDraftPlan() {
  return await fetch(process.env.VUE_APP_API_URL + "api/dashboard/plans/delete_draft_plan?user_id=" + localStorage.getItem('user_id') + "&plan_id=" + formData.id, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "vdas": process.env.VUE_APP_API_VDAS,
      "cp-token": process.env.VUE_APP_API_CP_TOKEN,
      "Authorization": "Bearer " + localStorage.getItem('token')
    }
  });
}

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

async function saveDraftPlan() {
  const processedImages = await Promise.all(
    formData.images.map(async (img, index) => {
      const num_image = img.num_image ?? (index + 1);
      const name = img.name || (img.file ? img.file.name : `image_${num_image}`);

      let image_url = '';
      if (img.is_new && img.file) {
        image_url = await fileToBase64(img.file);
      } else {
        image_url = img.image_url || (typeof img === 'string' ? img : '');
      }

      return {
        num_image,
        name,
        image_url
      };
    })
  );

  const planPayload = {
    ...(props.plan?.id && props.plan.id !== 0 && props.plan.id !== '0' ? { id_plan: props.plan.id } : {}),
    name: formData.name,
    description: formData.description,
    dates: date_type.value === 'any' ? null : createDatesJSON(formData.dates),
    capacity: formData.capacity,
    price: formData.price,
    expiration_date: date_type.value === 'any' ? formData.expiration_date : null,
    observations: formData.observations,
    images: processedImages
  };

  return await fetch(process.env.VUE_APP_API_URL+"api/dashboard/plans/set_plan_draft", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "vdas": process.env.VUE_APP_API_VDAS,
        "cp-token": process.env.VUE_APP_API_CP_TOKEN,
        "Authorization": "Bearer " + localStorage.getItem('token')
      },
      body: JSON.stringify({
        user_id: localStorage.getItem('user_id'),
        plan: planPayload
    })
  });
}

function createDatesJSON(dateItems) {
  if (!dateItems || dateItems.length === 0) return "";
  
  const data = dateItems.map(item => ({
    date: item[0],
    hour: item[1],
    title: item[2]
  }));
  
  return JSON.stringify(data);
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.fixed {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 500;
  padding: 16px;
  box-sizing: border-box;
}

.modal-card {
  background-color: #ffffff;
  padding: 24px;
  border-radius: var(--radius-md);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  width: 100%;
  max-width: 550px;
  max-height: 90vh;
  overflow-y: auto;
  box-sizing: border-box;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--very-clear-gray, #eeeeee);
  padding-bottom: 12px;
  margin-bottom: 18px;
}

.title {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-color-dark, #2f4858);
  margin: 0;
}

.close-btn {
  background: transparent;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: var(--clear-gray, #607d94);
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: color 0.2s, background-color 0.2s;
}

.close-btn:hover {
  color: var(--text-color-dark, #2f4858);
  background-color: var(--almost-white, #f5f5f5);
}

.form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
}

.form-group label {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-color-dark, #2f4858);
}

.required {
  color: #e53e3e;
}

.new-date-container {
  position: relative;
  background-color: var(--almost-white);
  padding: 16px 12px;
  border: 1px solid var(--light-gray, #dddddd);
  border-radius: var(--radius-md);
  margin-bottom: 10px;
}

.form-row {
  display: flex;
  gap: 12px;
}

.col {
  flex: 1;
}

textarea {
  font-family: "GatterSans";
  color: var(--text-color-dark);
  background-color: var(--white);
  border: 1px solid var(--light-gray);
  border-radius: var(--radius-md);
  font-size: 15px;
  padding: 12px 14px;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 80px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}

textarea:focus {
  border-color: var(--primary-color, #20cda8);
  box-shadow: 0 0 0 3px rgba(32, 205, 168, 0.1);
}

.images-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.upload-btn {
  font-size: 13px;
  font-weight: 600;
  color: var(--primary-color, #20cda8);
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px dashed var(--primary-color, #20cda8);
  background-color: rgba(32, 205, 168, 0.08);
  transition: background-color 0.2s, transform 0.1s;
}

.upload-btn:hover {
  background-color: rgba(32, 205, 168, 0.18);
}

.add-date-btn {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-color-dark);
  cursor: pointer;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1px dashed var(--primary-color, #20cda8);
  background-color: rgba(32, 205, 168, 0.08);
  transition: background-color 0.2s, transform 0.1s;
}

.add-date-btn:hover {
  background-color: rgba(32, 205, 168, 0.18);
}

.delete-date-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  color: var(--red);
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.2s, color 0.2s, transform 0.1s;
}

.delete-date-btn:hover {
  background-color: rgba(255, 107, 107, 0.18);
}

.hidden-input {
  display: none;
}

.empty-images-note {
  font-size: 13px;
  color: var(--clear-gray, #607d94);
  font-style: italic;
  margin: 6px 0 0 0;
}

.images-preview {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-top: 8px;
}

.image-thumb {
  position: relative;
  width: 100px;
  height: 100px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--light-gray, #dddddd);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
}

.image-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.remove-image-btn {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.65);
  color: #ffffff;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  transition: background-color 0.2s, transform 0.1s;
}

.remove-image-btn svg {
  display: block;
  pointer-events: none;
}

.remove-image-btn:hover {
  background-color: #e53e3e;
  transform: scale(1.1);
}

.form-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 10px;
  padding-top: 12px;
  border-top: 1px solid var(--very-clear-gray, #eeeeee);
}

.form-actions-left {
  display: flex;
  align-items: center;
}

.form-actions-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.delete-plan-btn {
  background-color: transparent;
  color: #e53e3e;
  border: 1.5px solid #feb2b2;
  border-radius: var(--radius-md, 8px);
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s, color 0.2s, transform 0.1s;
}

.delete-plan-btn:hover {
  background-color: #fff5f5;
  border-color: #fc8181;
  color: #c53030;
  transform: translateY(-1px);
}

.delete-plan-btn:active {
  transform: translateY(0);
}

.form-actions button {
  width: auto;
  min-width: 110px;
  padding: 10px 20px;
}

.radio-pill-group {
  display: flex;
  border: 1px solid var(--light-gray);
  background: var(--almost-white);
  padding: 4px;
  border-radius: var(--radius-md);
  gap: 4px;
}

.radio-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-weight: 600;
  color: var(--text-color-light);
  cursor: pointer;
  transition: all var(--transition-fast);
  user-select: none;
  width: 50%;
  justify-content: center;
}

.radio-pill input[type="radio"] {
  display: none;
}

.radio-pill.is-selected {
  background: #FFFFFF;
  color: var(--text-color-dark);
  box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
  border-radius: var(--radius-md);
}

.add-date-icon{
  width: 24px;
  height: 24px;
  cursor: pointer;
  transition: all var(--transition-fast);
  user-select: none;
}

.add-date-icon:hover{
  transform: scale(1.1);
}

.remove-date-icon{
  width: 24px;
  height: 24px;
  cursor: pointer;
  transition: all var(--transition-fast);
  user-select: none;
}

.remove-date-icon:hover{
  transform: scale(1.1);
}

</style>
  